import { HeaderBar } from 'components/HeaderBar';
import PageLayout from 'components/PageLayout';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useAuthState } from 'state/useAuthState';
import SupportUsIcon from 'assets/header-heart.svg?react';
import {
  matchPath,
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  useHistory,
} from 'react-router-dom';
import { useProgressionModel } from 'features/wildfires/hooks/useProgressionModel';
import { LoadingAndErrors } from 'components/LoadingAndErrors';
import { ProgressionModelMap } from 'features/wildfires/components/ProgressionModelMap';
import { ProgressionModelDrawerContent } from 'features/wildfires/components/ProgressionModelDrawerContent';
import {
  DrawerRefContent,
  MAP_ENTITY_DRAWER_MIN_SNAP_PCT,
  MapEntityDrawer,
} from 'components/Map/MapEntityDrawer';
import { ProgressionModelSlider } from 'features/wildfires/components/ProgressionModelSlider';
import { useCallback, useEffect, useRef } from 'react';
import { ProgressionModelDialog } from 'features/wildfires/components/ProgressionModelDialog';
import useSearchDrawerState from 'state/useSearchDrawerState';
import { useUpdateLayersConfig } from 'hooks/useUpdateLayersConfig';
import { useProgressionModelsEnabled } from 'features/wildfires/hooks/useProgressionModelsEnabled';
import {
  BackNavigationCallback,
  ShouldInterceptCondition,
  useInterceptBackNavigation,
} from 'hooks/useInterceptBackNavigation';
import { MapProvider } from 'react-map-gl';
import { useMapPinEnabled } from 'components/IncidentsMap/useMapPin';
import { MapPinDrawerContent } from 'components/Map/MapPinDrawerContent';
import { PROGRESSION_MODEL_LAYER_CONFIG } from 'features/wildfires/wildfire.constants';
import {
  MEMBERSHIP_PLANS,
  ROUTER_ENTITY_TO_ROUTE_PATH_MAP,
  WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH,
} from '../../constants';

type ProgressionModelContentProps = {
  geoEventId: number;
  modelId: number;
};

const MAP_DRAWER_ENTITY_SNAP_POINTS = [
  MAP_ENTITY_DRAWER_MIN_SNAP_PCT,
  0.47,
  0.7,
];

const ProgressionModelContent = (
  props: ProgressionModelContentProps,
): JSX.Element => {
  const { geoEventId, modelId } = props;
  const { open: searchDrawerOpen, setOpen: setSearchDrawerOpen } =
    useSearchDrawerState();
  const drawerRef = useRef<DrawerRefContent | null>(null);
  const searchDrawerOpenRef = useRef(searchDrawerOpen);
  const history = useHistory();
  const showMapPin = useMapPinEnabled();

  useUpdateLayersConfig(PROGRESSION_MODEL_LAYER_CONFIG);

  /**
   * This is important to correctly position the POI dialog within the map.
   * We save a reference to the initial open value using a ref so it won't
   * change on re-renders and restore this value when the component unmounts.
   */
  useEffect(() => {
    setSearchDrawerOpen(false);
    const initialOpenValue = searchDrawerOpenRef.current;
    return () => {
      setSearchDrawerOpen(initialOpenValue);
    };
  }, [setSearchDrawerOpen]);

  const handleBack: BackNavigationCallback = useCallback(
    (tx) => {
      history.push(tx.pathname + tx.search, { skipMapCentering: true });
    },
    [history],
  );

  const shouldIntercept: ShouldInterceptCondition = useCallback((location) => {
    const navMatch = matchPath(location.pathname, {
      path: ROUTER_ENTITY_TO_ROUTE_PATH_MAP.wildfire,
      exact: true,
    });
    return Boolean(navMatch) && !location.state;
  }, []);

  useInterceptBackNavigation({
    shouldIntercept,
    handleBack,
  });

  return (
    <MapProvider>
      <Switch>
        <Route
          exact
          path={WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH}
          render={() => (
            <>
              <ProgressionModelMap modelId={modelId} drawerRef={drawerRef} />

              <MapEntityDrawer
                snapPointsPct={MAP_DRAWER_ENTITY_SNAP_POINTS}
                defaultSnapPointPct={MAP_ENTITY_DRAWER_MIN_SNAP_PCT}
                open
                dismissible={false}
                preserveDrawerHeight={false}
                entityId={`progression-model-${modelId}`}
                ref={drawerRef}
              >
                <ProgressionModelDrawerContent
                  geoEventId={geoEventId}
                  modelId={modelId}
                />
              </MapEntityDrawer>

              <ProgressionModelDialog>
                <ProgressionModelSlider modelId={modelId} />
              </ProgressionModelDialog>
            </>
          )}
        />

        {showMapPin && (
          <Route
            exact
            path={[
              `${WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH}/@:lat,:lng,:zoom`,
              `${WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH}/@:lat,:lng`,
            ]}
            render={(routeProps) => (
              <>
                {/* This component must be a child of the Route
                    to access the route's params, which are needed
                    for the map pin marker to work correctly. */}
                <ProgressionModelMap modelId={modelId} drawerRef={drawerRef} />

                <MapEntityDrawer
                  ref={drawerRef}
                  snapPointsPct={MAP_DRAWER_ENTITY_SNAP_POINTS}
                  defaultSnapPointPct={0.47}
                  open
                  entityId={`mapPin-${routeProps.match.params}`}
                  // Make sure pathname doesn't already include lat, lng and zoom params
                  closeRedirectPath={
                    routeProps.location.pathname.split('/@')[0]
                  }
                  preventDrawerClose
                >
                  <MapPinDrawerContent />
                </MapEntityDrawer>
              </>
            )}
          />
        )}
      </Switch>
    </MapProvider>
  );
};

export const WildfireProgressionModel = (
  props: RouteComponentProps<{ geoEventId: string; modelId: string }>,
): JSX.Element => {
  const {
    match: { params },
  } = props;
  const { t } = useTranslation();
  const { showSupportUs } = useAuthState();
  const showProgressionModels = useProgressionModelsEnabled();

  const geoEventId = parseInt(params.geoEventId, 10);
  const modelId = parseInt(params.modelId, 10);

  const { isLoading, isError, progressionModel } = useProgressionModel({
    modelId,
  });

  const returnRoute = `/i/${geoEventId}?tab=info`;

  if (!showProgressionModels) {
    return (
      <Redirect
        to={{
          pathname: '/support_us/',
          search: `?plan=${MEMBERSHIP_PLANS.proYearly}`,
          state: { fromRoute: returnRoute },
        }}
      />
    );
  }

  const iconActions = showSupportUs
    ? [
        {
          label: t('header.actions.supportUs'),
          to: '/support_us',
          Icon: <SupportUsIcon />,
        },
      ]
    : undefined;

  if (isLoading || isError || !progressionModel) {
    return (
      <PageLayout data-cname="WildfireProgressionModel">
        {{
          content: <LoadingAndErrors isLoading={isLoading} />,
          headerBar: <HeaderBar action="back" dark iconActions={iconActions} />,
        }}
      </PageLayout>
    );
  }

  return (
    <PageLayout data-cname="WildfireProgressionModel">
      {{
        content: (
          <>
            <Helmet>
              <title>
                {t('wildfireProgressionModel.title', {
                  name: progressionModel.sourceAttribution,
                })}
              </title>
            </Helmet>
            <ProgressionModelContent
              geoEventId={geoEventId}
              modelId={modelId}
            />
          </>
        ),
        headerBar: (
          <HeaderBar action={returnRoute} dark iconActions={iconActions} />
        ),
      }}
    </PageLayout>
  );
};
